<!--
  TravelHistoryPanel.vue — track-history as go.lacak.io builds it: a SIDE PANEL in the widgets
  slot (not a bottom bar), matching the deployed travel-history-* UI. Structure mirrors the
  source (see docs/HISTORY-FIDELITY.md):
    · header  (title "History" + collapse + close)
    · date-range CHIP  (click → re-open "Select the period")
    · HISTORY | EVENTS tabs
    · toolbar  (play · colour · export KML · print · points-list · trips · settings)
    · content  (trip cards / points list / events)  +  playback timeline
  Controlled: the parent owns colourMode + settings (so the map and this panel never disagree);
  the panel owns the playback index and emits `seek`. Reconstructed from the live panel;
  internal impl re-authored.
-->
<template>
  <div class="travel-history" :class="{ shifted: objectsVisible }">
    <!-- header -->
    <div class="th-head">
      <i class="material-icons th-head-ico">history</i>
      <span class="th-title">History</span>
      <button class="th-icon-btn" v-tip="'Collapse'" @click="$emit('collapse')"><i class="material-icons">menu_open</i></button>
      <button class="th-icon-btn" v-tip="'Close'" @click="$emit('close')"><i class="material-icons">close</i></button>
    </div>

    <!-- date-range chip → re-open the period picker -->
    <button class="th-chip" @click="$emit('change-period')">
      <i class="material-icons">event</i><span>{{ rangeLabel }}</span>
    </button>

    <!-- tabs -->
    <div class="th-tabs">
      <button class="th-tab" :class="{ on: tab === 'history' }" @click="tab = 'history'">History</button>
      <button class="th-tab" :class="{ on: tab === 'events' }" @click="tab = 'events'">Events<span v-if="events.length" class="th-tab-count">{{ events.length }}</span></button>
    </div>

    <!-- toolbar -->
    <div class="th-tools">
      <button class="th-tool" :class="{ on: playing }" v-tip="playing ? 'Pause' : 'Play'" :disabled="!points.length" @click="togglePlay">
        <i class="material-icons">{{ playing ? 'pause' : 'play_arrow' }}</i>
      </button>

      <div class="th-tool-wrap">
        <button class="th-tool" :class="{ on: colorMenu }" v-tip="'Track colour'" @click.stop="colorMenu = !colorMenu; settingsMenu = false">
          <i class="material-icons">color_lens</i>
        </button>
        <div v-if="colorMenu" class="th-menu" @click.stop>
          <label v-for="m in colorModes" :key="m.key" class="th-menu-radio" @click="pickColor(m.key)">
            <i class="material-icons">{{ colorMode === m.key ? 'radio_button_checked' : 'radio_button_unchecked' }}</i>{{ m.label }}
          </label>
        </div>
      </div>

      <button class="th-tool" v-tip="'Export KML'" :disabled="!points.length" @click="$emit('export-kml')"><i class="material-icons">save_alt</i></button>
      <button class="th-tool" v-tip="'Print'" :disabled="!points.length" @click="$emit('print')"><i class="material-icons">print</i></button>
      <button class="th-tool" :class="{ on: view === 'points' }" v-tip="'Points list'" @click="view = view === 'points' ? 'trips' : 'points'"><i class="material-icons">view_headline</i></button>
      <button class="th-tool" :class="{ on: view === 'trips' }" v-tip="'Trips'" @click="view = 'trips'"><i class="material-icons">alt_route</i></button>

      <div class="th-tool-wrap th-tools-end">
        <button class="th-tool" :class="{ on: settingsMenu }" v-tip="'Settings'" @click.stop="settingsMenu = !settingsMenu; colorMenu = false">
          <i class="material-icons">settings</i>
        </button>
        <div v-if="settingsMenu" class="th-menu th-menu-right" @click.stop>
          <label v-for="s in settingKeys" :key="s.key" class="th-menu-check" @click="$emit('setting', s.key)">
            <i class="material-icons">{{ settings[s.key] ? 'check_box' : 'check_box_outline_blank' }}</i>{{ s.label }}
          </label>
        </div>
      </div>
    </div>

    <!-- content -->
    <div class="th-body">
      <template v-if="tab === 'history'">
        <!-- trips view — regular trip rows + interleaved parking rows -->
        <template v-if="view === 'trips'">
          <div v-if="!rows.length" class="th-empty">No trips in this period.</div>
          <template v-for="row in rows">
            <!-- regular trip: date-points · dumbbell · start address · mileage+duration · end address -->
            <div
              v-if="row.kind === 'trip'"
              :key="'t' + row.trip.startIdx"
              class="th-trip"
              :class="{ sel: selectedTrip === row.tripIndex }"
              @click="selectTrip(row.tripIndex, row.trip)"
            >
              <span class="th-trip-dot" :style="{ background: tripColor(row.tripIndex) }"></span>
              <div class="th-trip-body">
                <div class="th-trip-times">
                  <span class="th-trip-time">{{ hm(row.trip.from) }}</span>
                  <span class="th-dumbbell"></span>
                  <span class="th-trip-time">{{ hm(row.trip.to) }}</span>
                  <span class="th-trip-date">{{ dm(row.trip.from) }}</span>
                </div>
                <div class="th-addr"><i class="material-icons th-addr-a">trip_origin</i><span>{{ row.trip.startAddress }}</span></div>
                <div class="th-trip-metrics">
                  <span><i class="material-icons">directions</i>{{ row.trip.distanceKm.toFixed(1) }} km</span>
                  <span><i class="material-icons">timelapse</i>{{ durLabel(row.trip.durationMs) }}</span>
                </div>
                <div class="th-addr"><i class="material-icons th-addr-b">place</i><span>{{ row.trip.endAddress }}</span></div>
              </div>
              <i class="material-icons th-chevron">chevron_right</i>
            </div>
            <!-- parking: local_parking · address · parked duration -->
            <div v-else :key="'p' + row.stop.startIdx" class="th-park" @click="seekTo(row.stop.startIdx)">
              <i class="material-icons th-park-ico">local_parking</i>
              <div class="th-park-body">
                <div class="th-park-addr">{{ row.stop.address }}</div>
                <div class="th-park-sub">Parked {{ durLabel(row.stop.durationMs) }}</div>
              </div>
            </div>
          </template>
        </template>

        <!-- points view -->
        <template v-else>
          <div
            v-for="(p, i) in points"
            :key="i"
            class="th-point"
            :class="{ sel: index === i }"
            @click="seekTo(i)"
          >
            <span class="th-point-idx">{{ i + 1 }}</span>
            <span class="th-point-time">{{ timeLabel(p.time) }}</span>
            <span class="th-point-speed">{{ p.speed || 0 }} km/h</span>
          </div>
        </template>
      </template>

      <!-- events tab — each event has a visibility (eye) toggle that shows/hides its map marker -->
      <template v-else>
        <div v-if="!events.length" class="th-empty">There are no events for this period.</div>
        <div v-else class="th-ev-head">
          <span>Events on map</span>
          <button class="th-ev-all" v-tip="allEventsShown ? 'Hide all' : 'Show all'" @click="toggleAllEvents">
            <i class="material-icons">{{ allEventsShown ? 'visibility' : 'visibility_off' }}</i>
          </button>
        </div>
        <div
          v-for="(e, i) in events"
          :key="evKey(e, i)"
          class="th-event"
          :class="{ unread: !e.read, hidden: !isEventShown(e, i) }"
          @click="focusEvent(e)"
        >
          <i class="material-icons th-event-ico" :style="{ color: eventColor(e.type) }">{{ eventIcon(e.type) }}</i>
          <div class="th-event-body">
            <div class="th-event-main">{{ e.message || eventLabel(e.type) }}</div>
            <div class="th-event-sub"><span class="th-event-type">{{ eventLabel(e.type) }}</span><span>{{ timeLabel(e.time) }}</span></div>
          </div>
          <button class="th-ev-eye" v-tip="isEventShown(e, i) ? 'Hide on map' : 'Show on map'" @click.stop="toggleEventMarker(e, i)">
            <i class="material-icons">{{ isEventShown(e, i) ? 'visibility' : 'visibility_off' }}</i>
          </button>
        </div>
      </template>
    </div>

    <!-- playback timeline (travel-history-time-line-tool) -->
    <div v-if="points.length" class="th-timeline">
      <button class="th-play" v-tip="playing ? 'Pause' : 'Play'" @click="togglePlay"><i class="material-icons">{{ playing ? 'pause' : 'play_arrow' }}</i></button>
      <input class="th-slider" type="range" min="0" :max="max" v-model.number="index" @input="onSeek" />
      <div class="th-tl-info">
        <span class="th-tl-time">{{ currentTime }}</span>
        <span class="th-tl-speed">{{ currentSpeed }} km/h</span>
      </div>
    </div>
  </div>
</template>

<script>
import { splitTrips, tripColor } from './track-trips.js'

export default {
  name: 'TravelHistoryPanel',
  props: {
    points: { type: Array, default: () => [] },
    events: { type: Array, default: () => [] },
    rangeLabel: { type: String, default: '' },
    objectsVisible: { type: Boolean, default: true },
    colorMode: { type: String, default: 'tracks' },
    settings: { type: Object, default: () => ({ showStops: true, showLBS: false, clustered: false, smartFilter: false, splitByStops: true }) },
  },
  data() {
    return {
      tab: 'history',
      view: 'trips',
      index: 0,
      playing: false,
      timer: null,
      colorMenu: false,
      settingsMenu: false,
      selectedTrip: -1,
      // Per-event map-marker visibility (key → false when hidden). Empty = all shown (the real
      // events-layer starts with selectAll); toggling an eye sets a key false.
      eventHidden: {},
      // Labels VERBATIM from the deployed travel-history coloring / settings menus.
      colorModes: [
        { key: 'manual', label: 'Color manually' },
        { key: 'tracks', label: 'Color by tracks' },
        { key: 'status', label: 'Color by status' },
        { key: 'speed', label: 'Color by speed' },
      ],
      settingKeys: [
        { key: 'showStops', label: 'Show stops' },
        { key: 'showLBS', label: 'Show LBS' },
        { key: 'clustered', label: 'Clustered' },
        { key: 'smartFilter', label: 'Smart filter' },
        { key: 'splitByStops', label: 'Split by Stops' },
      ],
    }
  },
  computed: {
    max() { return Math.max(0, this.points.length - 1) },
    tripSplit() { return splitTrips(this.points, { splitByStops: this.settings.splitByStops !== false }) },
    trips() { return this.tripSplit.trips },
    stops() { return this.tripSplit.stops },
    // Trips + parkings interleaved chronologically (real app renders regular + parking rows
    // in one list). Each item carries its origin so the row can render + seek correctly.
    rows() {
      const items = []
      this.trips.forEach((t, i) => items.push({ kind: 'trip', trip: t, tripIndex: i, sortIdx: t.startIdx }))
      this.stops.forEach((s) => items.push({ kind: 'parking', stop: s, sortIdx: s.startIdx }))
      items.sort((a, b) => a.sortIdx - b.sortIdx)
      return items
    },
    cur() { return this.points[this.index] || {} },
    currentSpeed() { return this.cur.speed != null ? this.cur.speed : 0 },
    multiDay() {
      const p = this.points
      if (p.length < 2 || !p[0].time || !p[p.length - 1].time) return false
      try { return new Date(p[0].time).toDateString() !== new Date(p[p.length - 1].time).toDateString() } catch (e) { return false }
    },
    currentTime() {
      if (!this.cur.time) return '—'
      try {
        const d = new Date(this.cur.time)
        return this.multiDay ? d.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : d.toLocaleTimeString()
      } catch (e) { return this.cur.time }
    },
    allEventsShown() { return this.events.length > 0 && this.events.every((e, i) => this.isEventShown(e, i)) },
  },
  mounted() {
    this.$emit('seek', 0)
    // Close the colour / settings dropdowns on any outside click (the menus + their toggle
    // buttons use @click.stop, so this only fires for clicks elsewhere).
    this._onDocClick = () => { this.colorMenu = false; this.settingsMenu = false }
    document.addEventListener('click', this._onDocClick)
  },
  beforeDestroy() {
    this.stop()
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick)
    this.$emit('event-markers', []) // clear the map events-layer when the panel closes
  },
  watch: {
    // A new track (switched unit / new period) resets playback so the timer can't run past the
    // old index and desync the slider/marker.
    points() { this.stop(); this.index = 0; this.selectedTrip = -1; this.$emit('seek', 0) },
    // Split-by-Stops re-indexes the trip list, so a stale selectedTrip would highlight the wrong
    // trip (and disagree with the map fit) — clear the selection when the split changes.
    'settings.splitByStops'() { this.selectedTrip = -1 },
    // A new events feed resets visibility to all-shown (the real events-layer's selectAll) and
    // re-publishes the marker set.
    events() { this.eventHidden = {}; this.emitEventMarkers() },
    // Markers show only while the EVENTS tab is open; switching away hides them.
    tab() { this.emitEventMarkers() },
  },
  methods: {
    tripColor,
    timeLabel(t) {
      if (!t) return '—'
      try {
        const d = new Date(t)
        return this.multiDay ? d.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      } catch (e) { return String(t) }
    },
    durLabel(ms) {
      const m = Math.round((ms || 0) / 60000)
      if (m < 60) return m + ' min'
      const h = Math.floor(m / 60)
      return h + 'h ' + (m % 60) + 'm'
    },
    // HH:mm and DD.MM for the trip-row date-points (real travel-history-date format).
    hm(t) { try { return new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) } catch (e) { return '—' } },
    dm(t) { try { const d = new Date(t); return ('0' + d.getDate()).slice(-2) + '.' + ('0' + (d.getMonth() + 1)).slice(-2) } catch (e) { return '' } },
    onSeek() { if (this.playing) this.stop(); this.$emit('seek', this.index) },
    seekTo(i) { this.index = i; if (this.playing) this.stop(); this.$emit('seek', i) },
    seek(i) { this.seekTo(i) },
    selectTrip(i, trip) {
      this.selectedTrip = i
      this.seekTo(trip.startIdx)
      this.$emit('select-trip', trip)
    },
    pickColor(key) {
      this.colorMenu = false
      if (key !== this.colorMode) this.$emit('color-mode', key)
    },
    togglePlay() { this.playing ? this.stop() : this.play() },
    play() {
      if (!this.points.length) return
      if (this.index >= this.max) { this.index = 0; this.$emit('seek', 0) }
      this.playing = true
      this.timer = setInterval(() => {
        if (this.index >= this.max) { this.stop(); return }
        this.index += 1
        this.$emit('seek', this.index)
      }, 280)
    },
    stop() { this.playing = false; if (this.timer) { clearInterval(this.timer); this.timer = null } },
    // Material icon per event type (VERBATIM go.lacak.io vocabulary: online/offline/inzone/
    // outzone/poweron/poweroff/info + checkpoint_*/task_*/route_failed).
    eventIcon(type) {
      return ({
        online: 'wifi', offline: 'wifi_off', inzone: 'login', outzone: 'logout',
        poweron: 'power', poweroff: 'power_off', info: 'info',
        checkpoint_arrived: 'flag', checkpoint_completed: 'check_circle', checkpoint_failed: 'error',
        task_completed: 'task_alt', task_failed: 'cancel', route_failed: 'wrong_location',
      })[type] || 'notifications'
    },
    eventColor(type) {
      return ({
        online: '#2e9e4b', offline: '#e53935', inzone: '#1e88c7', outzone: '#f2960f',
        poweron: '#2e9e4b', poweroff: '#e53935', info: '#5c6bc0',
        checkpoint_arrived: '#1e88c7', checkpoint_completed: '#2e9e4b', checkpoint_failed: '#e53935',
        task_completed: '#2e9e4b', task_failed: '#e53935', route_failed: '#e53935',
      })[type] || '#90a4ae'
    },
    // Human label (fallback when an event carries no message). Title-cased from the type token.
    eventLabel(type) {
      return ({
        online: 'Online', offline: 'Offline', inzone: 'Entered zone', outzone: 'Exited zone',
        poweron: 'Power on', poweroff: 'Power off', info: 'Info',
      })[type] || String(type || 'Event').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
    },
    // Nearest track-point index to a time (ms). Track points are chronological, so a linear
    // scan for the smallest |Δt| is fine at these sizes.
    nearestIndexByTime(t) {
      const target = new Date(t).getTime()
      if (!Number.isFinite(target) || !this.points.length) return -1
      let best = -1; let bestD = Infinity
      for (let i = 0; i < this.points.length; i++) {
        const d = Math.abs(new Date(this.points[i].time).getTime() - target)
        if (d < bestD) { bestD = d; best = i }
      }
      return best
    },
    // Click an event → seek playback (this panel + the map, via the parent) to its moment.
    focusEvent(e) {
      const idx = this.nearestIndexByTime(e && e.time)
      if (idx < 0) return
      this.seekTo(idx)
      this.$emit('focus-event', idx)
    },
    // ---- event map markers (the events-layer + per-event eye toggle) ----
    evKey(e, i) { return e && e.id != null ? 'e' + e.id : 'i' + i },
    isEventShown(e, i) { return this.eventHidden[this.evKey(e, i)] !== true },
    toggleEventMarker(e, i) { this.$set(this.eventHidden, this.evKey(e, i), this.isEventShown(e, i)); this.emitEventMarkers() },
    toggleAllEvents() {
      const hideAll = this.allEventsShown
      const next = {}
      if (hideAll) this.events.forEach((e, i) => { next[this.evKey(e, i)] = true })
      this.eventHidden = next
      this.emitEventMarkers()
    },
    // Publish the currently-visible event markers to the parent (→ map events-layer). Only while
    // the EVENTS tab is open; an empty list clears the layer. Each marker carries its rendered
    // icon + colour so the map doesn't need to duplicate the event vocabulary.
    emitEventMarkers() {
      const list = []
      if (this.tab === 'events') {
        this.events.forEach((e, i) => {
          if (!e || !e.location || !Number.isFinite(e.location.lat) || !Number.isFinite(e.location.lng) || !this.isEventShown(e, i)) return
          list.push({
            id: this.evKey(e, i), lat: e.location.lat, lng: e.location.lng,
            icon: this.eventIcon(e.type), color: this.eventColor(e.type),
            title: e.message || this.eventLabel(e.type), sub: this.eventLabel(e.type),
            time: this.timeLabel(e.time), address: e.address || ''
          })
        })
      }
      this.$emit('event-markers', list)
    },
  },
}
</script>

<style scoped>
.travel-history {
  position: absolute; top: 12px; left: 60px; width: var(--panel-w, 344px); max-height: calc(100% - 24px);
  display: flex; flex-direction: column; background: #fff; border-radius: 8px;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.22); z-index: 15; overflow: hidden;
}
/* sit to the right of the Objects panel (left:12px, width:var(--panel-w)); use the SAME
   responsive width var so it never overlaps the list (a fixed 336px overlapped once --panel-w
   grew past it on wider viewports). */
.travel-history.shifted { left: calc(24px + var(--panel-w, 336px)); }

.th-head { display: flex; align-items: center; gap: 8px; padding: 12px 10px 12px 14px; border-bottom: 1px solid #eceff1; }
.th-head-ico { color: #37474f; font-size: 22px; }
.th-title { flex: 1; font-size: 18px; font-weight: 500; color: #263238; }
.th-icon-btn { background: none; border: none; cursor: pointer; color: #90a4ae; display: flex; padding: 2px; }
.th-icon-btn:hover { color: #546e7a; }

.th-chip { display: flex; align-items: center; gap: 7px; margin: 10px 10px 6px; padding: 8px 10px; background: #e8f4fb; border: none; border-radius: 6px; color: #1e88c7; font-size: 12px; font-weight: 500; cursor: pointer; text-align: left; }
.th-chip .material-icons { font-size: 18px; flex: none; }
/* keep the full "Month D, YYYY h:mm AM - …" range on one line like the real chip; ellipsis only
   if the panel is dragged narrow. */
.th-chip span { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.th-tabs { display: flex; border-bottom: 1px solid #eceff1; margin: 4px 0 0; }
.th-tab { flex: 1; background: none; border: none; padding: 10px 0; font-size: 13px; font-weight: 600; letter-spacing: .03em; text-transform: uppercase; color: #90a4ae; cursor: pointer; border-bottom: 2px solid transparent; }
.th-tab.on { color: #1e88c7; border-bottom-color: #1e88c7; }

.th-tools { display: flex; align-items: center; gap: 2px; padding: 6px 8px; border-bottom: 1px solid #eceff1; }
.th-tool-wrap { position: relative; display: flex; }
.th-tools-end { margin-left: auto; }
.th-tool { background: none; border: none; width: 34px; height: 34px; border-radius: 6px; cursor: pointer; color: #5f6368; display: flex; align-items: center; justify-content: center; }
.th-tool:hover { background: #f2f6f9; }
.th-tool.on { color: #1e88c7; background: #e8f4fb; }
.th-tool:disabled { color: #cfd8dc; cursor: default; background: none; }
.th-tool .material-icons { font-size: 20px; }

.th-menu { position: absolute; top: 38px; left: 0; min-width: 168px; background: #fff; border-radius: 6px; box-shadow: 0 3px 14px rgba(0, 0, 0, 0.24); padding: 4px 0; z-index: 20; }
.th-menu-right { left: auto; right: 0; }
.th-menu-radio, .th-menu-check { display: flex; align-items: center; gap: 8px; padding: 8px 14px; font-size: 14px; color: #37474f; cursor: pointer; white-space: nowrap; }
.th-menu-radio:hover, .th-menu-check:hover { background: #f2f6f9; }
.th-menu-radio .material-icons, .th-menu-check .material-icons { font-size: 18px; color: #607d8b; }

.th-body { flex: 1; overflow-y: auto; padding: 6px 0; min-height: 80px; }
.th-empty { padding: 24px 16px; text-align: center; color: #90a4ae; font-size: 13px; }

.th-trip { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px 10px 14px; cursor: pointer; border-left: 3px solid transparent; border-bottom: 1px solid #f4f6f8; }
.th-trip:hover { background: #f6fafb; }
.th-trip.sel { background: #e8f4fb; border-left-color: #1e88c7; }
.th-trip-dot { width: 12px; height: 12px; border-radius: 50%; flex: none; margin-top: 3px; }
.th-trip-body { flex: 1; min-width: 0; }
.th-trip-times { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #263238; }
.th-trip-time { font-weight: 500; }
.th-dumbbell { flex: none; width: 26px; height: 8px; position: relative; }
.th-dumbbell::before { content: ''; position: absolute; top: 3px; left: 0; right: 0; height: 2px; background: #cfd8dc; }
.th-dumbbell::after { content: ''; position: absolute; top: 1px; left: 0; width: 6px; height: 6px; border-radius: 50%; background: #78909c; box-shadow: 20px 0 0 #78909c; }
.th-trip-date { margin-left: auto; font-size: 12px; color: #b0bec5; }
.th-addr { display: flex; align-items: flex-start; gap: 6px; font-size: 12px; color: #607d8b; margin-top: 4px; line-height: 1.35; }
.th-addr .material-icons { font-size: 15px; flex: none; margin-top: 1px; }
.th-addr-a { color: #2e9e4b; }
.th-addr-b { color: #e53935; }
.th-trip-metrics { display: flex; gap: 14px; font-size: 12px; color: #78909c; margin: 4px 0 2px 21px; }
.th-trip-metrics span { display: flex; align-items: center; gap: 4px; }
.th-trip-metrics .material-icons { font-size: 14px; color: #b0bec5; }
.th-chevron { color: #cfd8dc; font-size: 20px; flex: none; align-self: center; }
.th-park { display: flex; align-items: center; gap: 10px; padding: 8px 12px 8px 16px; cursor: pointer; border-bottom: 1px solid #f4f6f8; background: #fafbfc; }
.th-park:hover { background: #f2f6f9; }
.th-park-ico { color: #546e7a; font-size: 20px; flex: none; }
.th-park-body { flex: 1; min-width: 0; }
.th-park-addr { font-size: 13px; color: #546e7a; line-height: 1.3; }
.th-park-sub { font-size: 12px; color: #90a4ae; margin-top: 1px; }

.th-point { display: flex; align-items: center; gap: 10px; padding: 6px 14px; cursor: pointer; font-size: 13px; color: #455a64; }
.th-point:hover { background: #f6fafb; }
.th-point.sel { background: #e8f4fb; }
.th-point-idx { width: 34px; color: #b0bec5; font-size: 11px; }
.th-point-time { flex: 1; }
.th-point-speed { color: #78909c; }

.th-tab-count { display: inline-block; margin-left: 6px; min-width: 16px; padding: 0 5px; height: 16px; line-height: 16px; border-radius: 8px; background: #cfd8dc; color: #fff; font-size: 10px; font-weight: 700; vertical-align: 1px; }
.th-tab.on .th-tab-count { background: #1e88c7; }
.th-ev-head { display: flex; align-items: center; justify-content: space-between; padding: 6px 12px 6px 14px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: .03em; color: #90a4ae; border-bottom: 1px solid #f0f2f4; }
.th-ev-all { background: none; border: none; cursor: pointer; color: #607d8b; display: flex; padding: 2px; }
.th-ev-all .material-icons { font-size: 18px; }
.th-event { display: flex; align-items: flex-start; gap: 10px; padding: 9px 14px; cursor: pointer; border-left: 3px solid transparent; }
.th-event:hover { background: #f6fafb; }
.th-event:hover .th-ev-eye { color: #607d8b; }
.th-event.unread { border-left-color: #1e88c7; background: #f4fafd; }
.th-event.hidden { opacity: .55; }
.th-event-ico { font-size: 20px; flex: none; margin-top: 1px; }
.th-event-body { flex: 1; min-width: 0; }
.th-event-main { font-size: 14px; color: #37474f; }
.th-event-sub { display: flex; gap: 10px; font-size: 12px; color: #90a4ae; margin-top: 2px; }
.th-event-type { color: #607d8b; font-weight: 500; }
.th-ev-eye { background: none; border: none; cursor: pointer; color: #b0bec5; display: flex; padding: 2px; flex: none; margin-top: 1px; }
.th-ev-eye:hover { color: #1e88c7; }

.th-timeline { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-top: 1px solid #eceff1; }
.th-play { background: #1e88c7; border: none; width: 34px; height: 34px; border-radius: 50%; color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; flex: none; }
.th-play .material-icons { font-size: 20px; }
.th-slider { flex: 1; min-width: 0; accent-color: #1e88c7; }
.th-tl-info { display: flex; flex-direction: column; align-items: flex-end; flex: none; min-width: 74px; }
.th-tl-time { font-size: 12px; color: #37474f; }
.th-tl-speed { font-size: 11px; color: #78909c; }
</style>
